import {
  createHttpClient,
  type HttpClient,
} from "@shared/http/createHttpClient";
import type { RequestContext } from "@shared/http/requestContext";

const http: HttpClient = createHttpClient();

export interface WorkspaceDocumentTypeInfo {
  type_key: string;
  label: string;
  report_key: string;
  source_tab: string;
  source_option: string;
  docx_document_type: string;
  library_section: "official_record" | "ai_report" | "quality_report";
}

export interface WorkspaceReportDto {
  id: string;
  report_key: string;
  document_type: string;
  template_key: string;
  title: string;
  state: string;
  content: string;
  source_tab: string;
  source_option: string;
  source_history: unknown[];
  updated_at: string | null;
  created_at: string | null;
  patient_name: string;
  document_type_label: string;
  docx_file_name: string;
  docx_error: string;
  library_section: "official_record" | "ai_report" | "quality_report";
  record_id?: string;
  patient_id?: string;
  visit_date?: string;
  source_kind: "current_medical_record" | "ai_generated" | "ai_promoted";
  review_status: "draft" | "reviewed" | "signed" | "returned";
  version: number;
  revision_history?: unknown[];
  /** 列表投影省略正文时为 false，需经详情接口补全 content/source_history */
  content_loaded?: boolean;
}

export interface WorkspaceReportListParams {
  include_content?: boolean;
  offset?: number;
  limit?: number;
  library_section?: WorkspaceReportDto["library_section"];
}

export interface WorkspaceReportListResponse {
  reports: WorkspaceReportDto[];
  total?: number;
  offset?: number;
  limit?: number;
}

export interface SaveWorkspaceReportRequest {
  report_key: string;
  document_type: string;
  template_key?: string;
  title: string;
  document_type_label: string;
  content: string;
  patient_name: string;
  source_tab: string;
  source_option: string;
  source_history: unknown[];
  library_section: "official_record" | "ai_report" | "quality_report";
  record_id?: string;
  patient_id?: string;
  visit_date?: string;
  source_kind: "current_medical_record" | "ai_generated" | "ai_promoted";
  review_status: "draft" | "reviewed" | "signed" | "returned";
  version?: number;
  edit_reason?: string;
}

export async function fetchWorkspaceDocumentTypes(): Promise<{
  types: WorkspaceDocumentTypeInfo[];
}> {
  return http.get("/workspace-reports/types") as unknown as Promise<{
    types: WorkspaceDocumentTypeInfo[];
  }>;
}

export async function fetchWorkspaceReports(
  params: WorkspaceReportListParams = {},
): Promise<WorkspaceReportListResponse> {
  return http.get("/workspace-reports/", {
    params,
  }) as unknown as Promise<WorkspaceReportListResponse>;
}

export async function fetchWorkspaceReport(
  reportId: string,
): Promise<WorkspaceReportDto> {
  return http.get(
    `/workspace-reports/${reportId}`,
  ) as unknown as Promise<WorkspaceReportDto>;
}

export async function saveWorkspaceReport(
  request: SaveWorkspaceReportRequest,
  requestContext?: RequestContext,
): Promise<WorkspaceReportDto> {
  return http.post(
    "/workspace-reports/",
    request,
    requestContext?.headers ? { headers: requestContext.headers } : undefined,
  ) as unknown as Promise<WorkspaceReportDto>;
}

export async function updateWorkspaceReport(
  reportId: string,
  content: string,
  options: { edit_reason?: string; review_status?: string } = {},
): Promise<WorkspaceReportDto> {
  return http.put(`/workspace-reports/${reportId}`, {
    content,
    ...options,
  }) as unknown as Promise<WorkspaceReportDto>;
}

export async function deleteWorkspaceReport(
  reportId: string,
): Promise<{ success: boolean }> {
  return http.delete(`/workspace-reports/${reportId}`) as unknown as Promise<{
    success: boolean;
  }>;
}

export async function downloadWorkspaceReport(reportId: string): Promise<Blob> {
  return http.get(`/workspace-reports/${reportId}/download`, {
    responseType: "blob",
  }) as unknown as Promise<Blob>;
}
